import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Loading } from '../../atoms'
import type {
  PropertyPanelMarketContentProps,
  MarketStats,
  ChartData,
} from './PropertyPanelMarketContent.types'
import {
  MarketContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
} from './PropertyPanelMarketContent.styles'

const PropertyTypeSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding: 4px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
`

const PropertyTypeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.background.primary : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme, $active }) =>
    $active
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

interface MarketService {
  getMarketStats: (params: any) => Promise<MarketStats>
  getPriceHistory: (params: any) => Promise<{
    priceData: ChartData
    pricePerSqmData: ChartData
  }>
  getMarketComparison: (params: any) => Promise<{
    saleComparisons: any[]
    rentComparisons: any[]
  }>
  deriveStatsFromChartData: (
    priceData?: ChartData,
    pricePerSqmData?: ChartData
  ) => MarketStats
}

// eslint-disable-next-line max-len
interface PropertyPanelMarketContentWithServiceProps extends PropertyPanelMarketContentProps {
  marketService: MarketService
}

type PropertyTypeOption = 'apartment' | 'house'

export const PropertyPanelMarketContent: React.FC<
  PropertyPanelMarketContentWithServiceProps
> = ({
  entity,
  entityType,
  marketService,
  MarketOverview,
  PriceHistoryChart,
  PricePerSqmChart,
  MarketComparison,
  RecentListings,
}) => {
  const { t } = useTranslation('explore')
  const [marketStats, setMarketStats] = useState<MarketStats | null>(null)
  const [priceChartData, setPriceChartData] = useState<ChartData | null>(null)
  const [pricePerSqmChartData, setPricePerSqmChartData] =
    useState<ChartData | null>(null)
  const [comparisonData, setComparisonData] = useState<{
    saleComparisons: any[]
    rentComparisons: any[]
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPropertyType, setSelectedPropertyType] =
    useState<PropertyTypeOption>('apartment')

  // Helper function to get property type
  const getPropertyType = (): string => {
    return selectedPropertyType === 'apartment' ? 'Apartment' : 'House'
  }

  // Helper function to get empty chart data structure
  const getEmptyChartData = (): ChartData => ({
    sale: {
      '1A': { dates: [], values: [] },
      '3A': { dates: [], values: [] },
      '5A': { dates: [], values: [] },
      '10A': { dates: [], values: [] },
    },
    rent: {
      '1A': { dates: [], values: [] },
      '3A': { dates: [], values: [] },
      '5A': { dates: [], values: [] },
      '10A': { dates: [], values: [] },
    },
  })

  useEffect(() => {
    const fetchMarketData = async () => {
      setLoading(true)
      setError(null)
      try {
        const marketParams = {
          postalCode: entity.address_postal_code_name,
          municipalityName: entity.municipality_name,
          districtName: entity.district_name,
          regionName: entity.region_name,
          propertyType: getPropertyType(),
        }

        // Fetch price history and comparison data in parallel
        const [priceHistory, comparison] = await Promise.all([
          marketService.getPriceHistory(marketParams),
          marketService.getMarketComparison(marketParams),
        ])

        const priceData = priceHistory.priceData || getEmptyChartData()
        const pricePerSqmData =
          priceHistory.pricePerSqmData || getEmptyChartData()

        // Derive market stats from chart data to ensure consistency
        // This uses the same weighted progressive data as the charts
        const stats = marketService.deriveStatsFromChartData(
          priceHistory.priceData,
          priceHistory.pricePerSqmData
        )

        setMarketStats(stats)
        setPriceChartData(priceData)
        setPricePerSqmChartData(pricePerSqmData)
        setComparisonData(comparison)
      } catch (err) {
        console.error('Error fetching market data:', err)
        setError('Failed to load market data')
      } finally {
        setLoading(false)
      }
    }

    if (entity.address_postal_code_name || entity.municipality_name) {
      fetchMarketData()
    } else {
      setLoading(false)
    }
  }, [
    entity.address_postal_code_name,
    entity.municipality_name,
    entity.district_name,
    entity.region_name,
    entityType,
    selectedPropertyType,
  ])

  if (!entity.address_postal_code_name && !entity.municipality_name) {
    return (
      <EmptyState>
        <EmptyIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 3h18v18H3z" />
            <path d="M8 12h8M8 16h8M8 8h8" />
          </svg>
        </EmptyIcon>
        <EmptyTitle>{t('propertyPanel.market.noMarketData')}</EmptyTitle>
        <EmptyDescription>
          {t('propertyPanel.market.locationRequired')}
        </EmptyDescription>
      </EmptyState>
    )
  }

  if (loading) {
    return (
      <MarketContainer>
        <Loading />
      </MarketContainer>
    )
  }

  if (error || !marketStats) {
    return (
      <EmptyState>
        <EmptyIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </EmptyIcon>
        <EmptyTitle>{t('propertyPanel.market.noMarketData')}</EmptyTitle>
        <EmptyDescription>
          {error || t('propertyPanel.market.dataUnavailable')}
        </EmptyDescription>
      </EmptyState>
    )
  }

  return (
    <MarketContainer>
      <PropertyTypeSelector>
        <PropertyTypeButton
          $active={selectedPropertyType === 'apartment'}
          onClick={() => setSelectedPropertyType('apartment')}
        >
          {t('propertyPanel.market.apartment')}
        </PropertyTypeButton>
        <PropertyTypeButton
          $active={selectedPropertyType === 'house'}
          onClick={() => setSelectedPropertyType('house')}
        >
          {t('propertyPanel.market.house')}
        </PropertyTypeButton>
      </PropertyTypeSelector>

      <MarketOverview
        postalCode={entity.address_postal_code_name}
        municipalityName={entity.municipality_name}
        marketStats={marketStats}
      />

      <PricePerSqmChart
        chartData={pricePerSqmChartData || getEmptyChartData()}
        postalCodeName={entity.address_postal_code_name}
      />

      <PriceHistoryChart
        chartData={priceChartData || getEmptyChartData()}
        postalCodeName={entity.address_postal_code_name}
      />

      <MarketComparison
        saleComparisons={comparisonData?.saleComparisons}
        rentComparisons={comparisonData?.rentComparisons}
        loading={loading}
      />

      <RecentListings
        postalCode={entity.address_postal_code_name}
        municipalityName={entity.municipality_name}
        propertyType={
          selectedPropertyType === 'apartment' ? 'Apartment' : 'House'
        }
      />
    </MarketContainer>
  )
}
